import { getIsServerSide } from './isServerSide';

type ConfigureApiContextDomainParams = {
  authApiContextDomainEnabled?: boolean;
};

type ApiDomainEnvironment = {
  apiDomainName?: string;
};

type AuthDomainEnvironment = {
  authenticationBaseUrl?: string;
};

const AUTH_SERVICE_PATH = 'user-account-service';

/** First path segments of the VV services the browser calls directly. */
const API_SERVICE_PATHS = [AUTH_SERVICE_PATH, 'dxpcore'];

const apiContextDomainState: Required<ConfigureApiContextDomainParams> = {
  authApiContextDomainEnabled: false,
};

const hasTrailingSlash = (url = '') => url.endsWith('/');

const stripTrailingSlash = (url = '') => url.replace(/\/+$/g, '');

const concatUrlPath = (baseUrl = '', path = '') => {
  const normalizedPath = path.replace(/^\/+/g, '');

  return normalizedPath ? `${stripTrailingSlash(baseUrl)}/${normalizedPath}` : stripTrailingSlash(baseUrl);
};

const getBrowserOrigin = () => {
  if (getIsServerSide()) {
    return '';
  }

  return window.location?.origin ?? '';
};

export const configureApiContextDomain = ({ authApiContextDomainEnabled }: ConfigureApiContextDomainParams): void => {
  if (getIsServerSide()) {
    return;
  }

  apiContextDomainState.authApiContextDomainEnabled = Boolean(authApiContextDomainEnabled);
};

export const getIsApiContextDomainEnabled = (): boolean => apiContextDomainState.authApiContextDomainEnabled;

/**
 * Resolves the API domain the browser should call, e.g. `https://dev.virginvoyages.com` for
 * `${domain}/graphql`. Server-side and flag-off reads are returned unchanged.
 */
export const resolveApiDomainName = ({ apiDomainName = '' }: ApiDomainEnvironment): string => {
  if (getIsServerSide() || !getIsApiContextDomainEnabled()) {
    return apiDomainName;
  }

  const browserOrigin = getBrowserOrigin();

  if (!browserOrigin) {
    return apiDomainName;
  }

  return hasTrailingSlash(apiDomainName) ? `${browserOrigin}/` : browserOrigin;
};

/**
 * Swaps the configured API domain for the browser origin in an already built base URL,
 * preserving the service path, e.g. `https://app.dev.virginvoyages.com/bookvoyage-bff` ->
 * `https://dev.virginvoyages.com/bookvoyage-bff`.
 */
export const resolveApiBaseUrl = (baseUrl: string, { apiDomainName = '' }: ApiDomainEnvironment): string => {
  if (getIsServerSide() || !getIsApiContextDomainEnabled() || !apiDomainName) {
    return baseUrl;
  }

  const browserOrigin = getBrowserOrigin();

  if (!browserOrigin) {
    return baseUrl;
  }

  const normalizedApiDomainName = stripTrailingSlash(apiDomainName);

  return baseUrl.startsWith(normalizedApiDomainName)
    ? `${browserOrigin}${baseUrl.slice(normalizedApiDomainName.length)}`
    : baseUrl;
};

const getUrlOrigin = (url = '') => {
  try {
    return new URL(url).origin;
  } catch {
    return '';
  }
};

const getUrlServicePath = (url = '') => {
  try {
    return new URL(url).pathname.split('/').filter(Boolean)[0] ?? '';
  } catch {
    return '';
  }
};

const isApiServiceUrl = (url: string) => API_SERVICE_PATHS.includes(getUrlServicePath(url));

/**
 * The auth SDK builds every request from the fully qualified URLs the startup call returns
 * (`emailSignInUrl`, `signUpUrl`, ...), so the browser origin swap has to be applied to those
 * values themselves. The startup service advertises its own host whatever host serves the call
 * (`https://app.dev.virginvoyages.com/...` on `https://dev.virginvoyages.com`), so values are
 * matched by service path rather than against `AUTHENTICATION_BASE_URL`. Navigation targets,
 * third-party endpoints and non-URL values (e.g. `clientToken`) are left untouched.
 */
export const resolveStartupConfigUrls = <T extends object>(startupConfig: T): T => {
  if (getIsServerSide() || !getIsApiContextDomainEnabled() || !startupConfig) {
    return startupConfig;
  }

  const browserOrigin = getBrowserOrigin();

  if (!browserOrigin) {
    return startupConfig;
  }

  const entries = Object.entries(startupConfig as Record<string, unknown>).map(([key, value]) => {
    if (typeof value !== 'string' || !isApiServiceUrl(value)) {
      return [key, value];
    }

    const valueOrigin = getUrlOrigin(value);

    if (!valueOrigin || valueOrigin === browserOrigin) {
      return [key, value];
    }

    return [key, `${browserOrigin}${value.slice(valueOrigin.length)}`];
  });

  return Object.fromEntries(entries) as T;
};

/**
 * Resolves the auth SDK base URL to `${browserOrigin}/user-account-service`, keeping the
 * trailing-slash shape of the configured value so appended paths stay valid.
 */
export const resolveAuthBaseUrl = ({ authenticationBaseUrl = '' }: AuthDomainEnvironment): string => {
  if (getIsServerSide() || !getIsApiContextDomainEnabled()) {
    return authenticationBaseUrl;
  }

  const browserOrigin = getBrowserOrigin();

  if (!browserOrigin) {
    return authenticationBaseUrl;
  }

  const resolved = concatUrlPath(browserOrigin, AUTH_SERVICE_PATH);

  return hasTrailingSlash(authenticationBaseUrl) ? `${resolved}/` : resolved;
};
